import { useMutation } from '@apollo/client';
import { Button, Dialog, DialogProps, FileUpload, Icon, TextField } from '@elipssolution/harfang';
import { mdiSendOutline } from '@mdi/js';
import { Stack, Typography, styled } from '@mui/material';
import getConfig from 'next/config';
import { useSession as useNextAuthSession } from 'next-auth/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { browserName, browserVersion } from 'react-device-detect';
import { Controller, useForm } from 'react-hook-form';
import sanitize from 'sanitize-html';

import SupportItem from './SupportItem';
import SupportModuleItem from './SupportModuleItem';
import UploadedImages from './UploadedImages';
import { useConfirmationDialogContext } from '../../../components/ConfirmationDialogProvider';
import useFormat from '../../../hooks/useFormat';
import { ModuleCodeEnum } from '../../../types/widget';
import { renderFullName } from '../../../utils/renderFullName';
import { SUCCESS_ANIMATION_DELAY } from '../../../utils/statusAnimationDelay';
import { SEND_SUGGESTIONS_MAIL, SendSuggestionsMailType } from '../../api/suggestion';
import useCustomRouter from '../../hooks/useCustomRouter';
import { useModules } from '../../hooks/useModules';
import { PublicRuntimeConfigType } from '../../types/publicRuntimeConfig';
import { RequestTypeEnum, SupportFormType } from '../../types/support';
import { useSession } from '../session/SessionContext';

const SUPPORT_HEADER_HEIGHT = 49;

const BOTTOM_BAR_HEIGHT = 49;

const PADDING_HEIGHT = 8;

const AUTOCOMPLETE_MODULE_HEIGHT = 50;

const MAX_FILES = 5;

const StyledSupportHeader = styled(Stack)(({ theme: { palette } }) => ({
	position: 'fixed',
	top: 0,
	justifyContent: 'center',
	width: '100%',
	backgroundColor: palette.background.default,
	height: SUPPORT_HEADER_HEIGHT,
	zIndex: 2,
}));

const SuggestionTextField = styled(TextField)({
	'& > div > textarea': {
		minHeight: 150,
	},
});

const StyledDialog = styled(Dialog)({
	'&.MuiDialog-root .MuiDialogContent-root': {
		paddingBottom: 0,
	},
});

type SupportProps = {
	isDialogOpen?: DialogProps['open'];
	onDialogClose?: () => void;
};

const Support = ({ isDialogOpen = false, onDialogClose }: SupportProps) => {
	const {
		publicRuntimeConfig: { appName },
	} = getConfig() as PublicRuntimeConfigType;

	const { isMobileFormat } = useFormat();
	const { replace } = useCustomRouter();
	const availableModules = useModules();
	const { confirmCloseDialog } = useConfirmationDialogContext();

	const {
		user: { email, firstName, lastName },
		customerFile,
	} = useSession();
	const { data } = useNextAuthSession();
	const { access_token } = data ?? {};

	const {
		control,
		formState: { isValid, isDirty },
		handleSubmit,
		reset,
		setValue,
		watch,
	} = useForm<SupportFormType>();

	const moreInfoRef = useRef<HTMLDivElement>(null);
	const supportIntroductionRef = useRef<HTMLDivElement>(null);
	const formContentContainerRef = useRef<HTMLDivElement>(null);
	const formContentRef = useRef<HTMLDivElement>(null);
	const supportContentRef = useRef<HTMLDivElement>(null);

	const formType = watch('type');
	const formImages = watch('images');
	const formModule = watch('module');

	const signModule = useMemo(
		() => availableModules?.find(({ code }) => code === ModuleCodeEnum.SIGN),
		[availableModules],
	);

	const clientInformations = useMemo(
		() =>
			`
			<p><strong>Nom de l'utilisateur :</strong> ${renderFullName({ firstName, lastName })}</p>
			<p><strong>Email de l'utilisateur :</strong> ${email}</p>
			${
				formModule?.code !== ModuleCodeEnum.BROCHURE
					? `<p><strong>Dossier :</strong> ${customerFile?.code ?? ''} - ${customerFile?.name ?? ''}</p>`
					: ''
			}
			<p><strong>Navigateur :</strong> ${browserName}@${browserVersion}</p>
			<p><strong>Module concerné :</strong> ${
				formType === RequestTypeEnum.PURCHASE_SIGNATURE_INVITATIONS ? signModule?.name ?? 'Signature' : formModule?.name
			}</p><br/>`,
		[
			customerFile?.code,
			customerFile?.name,
			email,
			firstName,
			formModule?.code,
			formModule?.name,
			formType,
			lastName,
			signModule?.name,
		],
	);

	const [hasSendTicketSucceeded, setHasSendTicketSucceeded] = useState(false);
	const [sendTicketError, setSendTicketError] = useState<string>();

	const [
		sendEnhancementSuggestion,
		{ data: sendSuggestionsData, loading: isMailSending, error: sendSuggestionsError, reset: resetSendSuggestionsData },
	] = useMutation<SendSuggestionsMailType>(SEND_SUGGESTIONS_MAIL);

	const closeDialog = useCallback(() => {
		onDialogClose?.();

		setHasSendTicketSucceeded(false);
		setSendTicketError(undefined);
		resetSendSuggestionsData();

		reset({
			type: null,
			message: null,
			images: [],
		});
	}, [onDialogClose, reset, resetSendSuggestionsData]);

	const handleCloseEvent = () => {
		(async () => {
			if (isDirty) {
				const isCloseConfirmed = await confirmCloseDialog({
					title: 'Êtes-vous sûr de vouloir abandonner votre demande ?',
				});

				if (!isCloseConfirmed) return;
			}

			closeDialog();
		})().catch((closeDialogError) => {
			throw closeDialogError;
		});
	};

	const createTicket = useCallback(
		async ({
			description,
			imagesToUpload,
			moduleCode,
			type,
		}: {
			description: SupportFormType['message'];
			imagesToUpload: SupportFormType['images'];
			moduleCode: ModuleCodeEnum;
			type: SupportFormType['type'];
		}) => {
			if (!access_token || !description) return;

			const headers = new Headers();
			headers.append('Authorization', `Bearer ${access_token}`);
			if (customerFile?.id) headers.append('customer-file-id', customerFile.id);

			const body = new FormData();

			if (imagesToUpload && type === RequestTypeEnum.BUG) {
				imagesToUpload.forEach((imageToUpload) => body.append('files', imageToUpload));
			}

			const subject = `${appName}${
				moduleCode !== ModuleCodeEnum.BROCHURE && customerFile?.code ? ` - ${customerFile.code}` : ''
			} - ${type === RequestTypeEnum.PURCHASE_SIGNATURE_INVITATIONS ? "Achat d'invitations à signer" : 'Bug'}`;

			const ticketingURL = new URL('/ticketing', window.location.href);
			ticketingURL.searchParams.append('subject', subject);
			ticketingURL.searchParams.append('description', description);
			if (type === RequestTypeEnum.PURCHASE_SIGNATURE_INVITATIONS)
				ticketingURL.searchParams.append('isPurchaseSignatureInvitationsRequest', 'true');
			ticketingURL.searchParams.append('moduleCode', moduleCode);

			const response = await fetch(ticketingURL, {
				method: 'POST',
				headers,
				...(imagesToUpload && { body }),
			});

			if (!response.ok) {
				throw new Error('Unable to submit ticket');
			}

			setHasSendTicketSucceeded(true);

			setTimeout(() => {
				if (isMobileFormat) {
					replace('/').catch((replaceError) => {
						throw replaceError;
					});
				} else closeDialog();
			}, SUCCESS_ANIMATION_DELAY);
		},
		[access_token, customerFile?.id, customerFile?.code, appName, isMobileFormat, closeDialog, replace],
	);

	const onSubmit = useCallback(
		async ({ images, message, module, type }: SupportFormType) => {
			if (!message) return;

			const suggestionMessage = `${clientInformations}<p>${sanitize(message)}</p>`;

			if (type === RequestTypeEnum.ENHANCEMENT) {
				await sendEnhancementSuggestion({
					variables: {
						sendSuggestionsMailInput: {
							suggestions: suggestionMessage,
						},
					},
				});
				setTimeout(() => {
					if (isMobileFormat) {
						replace('/').catch((replaceError) => {
							throw replaceError;
						});
					} else closeDialog();
				}, SUCCESS_ANIMATION_DELAY);
			}

			if (type === RequestTypeEnum.BUG || type === RequestTypeEnum.PURCHASE_SIGNATURE_INVITATIONS) {
				const { code: moduleCode } = module ?? {};

				await createTicket({
					imagesToUpload: images,
					description: suggestionMessage,
					moduleCode: type === RequestTypeEnum.PURCHASE_SIGNATURE_INVITATIONS ? ModuleCodeEnum?.SIGN : moduleCode,
					type,
				});
			}
		},
		[clientInformations, sendEnhancementSuggestion, isMobileFormat, closeDialog, replace, createTicket],
	);

	const scrollToBottom = () => {
		setTimeout(() => {
			supportContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
		}, 50);
	};

	const isSignModuleAvailable = useMemo(
		() => availableModules.some(({ code }) => code === ModuleCodeEnum.SIGN),
		[availableModules],
	);

	const supportButtonLabel = useMemo(() => {
		if (formType === RequestTypeEnum.ENHANCEMENT)
			return sendSuggestionsData ? 'Suggestion envoyée' : 'Envoyer la suggestion';

		return hasSendTicketSucceeded ? 'Demande envoyée' : 'Envoyer la demande';
	}, [formType, hasSendTicketSucceeded, sendSuggestionsData]);

	const supportButtonError = useMemo(() => {
		if (formType === RequestTypeEnum.ENHANCEMENT)
			return sendSuggestionsError ? "Erreur lors de l'envoi des suggestions" : undefined;

		return sendTicketError;
	}, [formType, sendSuggestionsError, sendTicketError]);

	const moreInfoHeight = useMemo(
		() =>
			isMobileFormat && !(formModule && !!formType)
				? window.innerHeight -
				  SUPPORT_HEADER_HEIGHT -
				  PADDING_HEIGHT * 2 -
				  BOTTOM_BAR_HEIGHT -
				  (supportIntroductionRef.current?.clientHeight ?? 0)
				: undefined,
		[formModule, formType, isMobileFormat],
	);

	const moduleInfoHeight = useMemo(
		() =>
			isMobileFormat && !((formImages?.length ?? -1) > 0)
				? window.innerHeight -
				  SUPPORT_HEADER_HEIGHT -
				  PADDING_HEIGHT * 5 -
				  BOTTOM_BAR_HEIGHT -
				  AUTOCOMPLETE_MODULE_HEIGHT
				: undefined,
		[formImages?.length, isMobileFormat],
	);

	const hasSendTicketOrSuggestionError = useMemo(
		() => !!sendTicketError || !!sendSuggestionsError,
		[sendTicketError, sendSuggestionsError],
	);

	const hasSendTicketOrSuggestionSucceeded = useMemo(
		() => !!hasSendTicketSucceeded || !!sendSuggestionsData,
		[hasSendTicketSucceeded, sendSuggestionsData],
	);
	const supportContent = useMemo(
		() => (
			<>
				<SupportItem
					content={
						<Stack gap={2} marginTop={isMobileFormat ? 4 : undefined}>
							<Typography>
								Merci de prendre le temps de nous faire part de votre demande. Nous sommes constamment à la recherche de
								moyens pour améliorer et faire évoluer {appName}, et votre retour nous est précieux !
							</Typography>
							<Typography>
								Avant de commencer, pourriez-vous nous indiquer si votre demande concerne une évolution
								{isSignModuleAvailable && !isMobileFormat
									? ", un bug ou une demande d'achat d'invitations à signer"
									: ' ou un bug'}
								?
							</Typography>
						</Stack>
					}
					action={
						<Stack flexDirection="row" justifyContent="flex-end" gap={1} marginTop={2} ref={supportIntroductionRef}>
							<Controller
								control={control}
								name="type"
								render={({ field: { onChange } }) => {
									const handleChange = (newType: RequestTypeEnum) => {
										if (newType !== formType) {
											reset();
											onChange(newType);
										}
									};

									return (
										<>
											<Button
												color={formType === RequestTypeEnum.ENHANCEMENT ? 'primary' : 'inherit'}
												onClick={() => handleChange(RequestTypeEnum.ENHANCEMENT)}
												variant={formType === RequestTypeEnum.ENHANCEMENT ? 'contained' : 'outlined'}
											>
												Evolution
											</Button>
											<Button
												color={formType === RequestTypeEnum.BUG ? 'primary' : 'inherit'}
												onClick={() => handleChange(RequestTypeEnum.BUG)}
												variant={formType === RequestTypeEnum.BUG ? 'contained' : 'outlined'}
											>
												Bug
											</Button>
											{isSignModuleAvailable && !isMobileFormat && (
												<Button
													color={formType === RequestTypeEnum.PURCHASE_SIGNATURE_INVITATIONS ? 'primary' : 'inherit'}
													onClick={() => {
														handleChange(RequestTypeEnum.PURCHASE_SIGNATURE_INVITATIONS);
													}}
													variant={
														formType === RequestTypeEnum.PURCHASE_SIGNATURE_INVITATIONS ? 'contained' : 'outlined'
													}
												>
													Achat d’invitations à signer
												</Button>
											)}
										</>
									);
								}}
								rules={{ required: true }}
							/>
						</Stack>
					}
				/>
				{formType && (
					<Stack ref={moreInfoRef} height={moreInfoHeight}>
						<SupportModuleItem control={control} onModuleChange={scrollToBottom} />
						{isMobileFormat && formType === RequestTypeEnum.PURCHASE_SIGNATURE_INVITATIONS && (
							<Button
								disabled={!isValid}
								error={hasSendTicketOrSuggestionError}
								loading={isMailSending}
								onClick={handleSubmit(onSubmit)}
								persistentErrorMessage={supportButtonError}
								startIcon={<Icon path={mdiSendOutline} size="small" />}
								success={hasSendTicketSucceeded}
								variant="contained"
							>
								{supportButtonLabel}
							</Button>
						)}
					</Stack>
				)}
				{formModule && (
					<Stack
						ref={formContentContainerRef}
						height={
							(formContentRef?.current?.clientHeight ?? 0) < (moduleInfoHeight ?? 0) ? moduleInfoHeight : undefined
						}
					>
						<Stack ref={formContentRef} gap={2}>
							{formType === RequestTypeEnum.ENHANCEMENT && (
								<SupportItem
									content={
										<Typography>
											Veuillez être le plus exhaustif possible afin que vos idées soient compréhensibles, interprétables
											et réalisables.
										</Typography>
									}
									action={
										<Stack marginTop={2}>
											<Controller
												control={control}
												name="message"
												render={({ field: { value, ...field } }) => (
													<SuggestionTextField
														{...field}
														disabled={!!sendSuggestionsError}
														placeholder="Votre suggestion..."
														value={value ?? ''}
														multiline
													/>
												)}
												rules={{ required: true }}
											/>
										</Stack>
									}
								/>
							)}
							{formType === RequestTypeEnum.BUG && (
								<SupportItem
									content={
										<Typography>
											Veuillez nous fournir le plus de détails possible afin de rendre votre bug reproductible et
											faciliter sa résolution (étapes de reproduction, capture d&apos;écran, etc.).
										</Typography>
									}
									action={
										<Stack gap={1} marginTop={2}>
											<Controller
												control={control}
												name="message"
												render={({ field: { value, ...field } }) => (
													<SuggestionTextField
														{...field}
														disabled={!!sendTicketError}
														placeholder="Description du bug..."
														value={value ?? ''}
														multiline
													/>
												)}
												rules={{ required: true }}
											/>
											{(formImages?.length ?? 0) < MAX_FILES && (
												<Controller
													control={control}
													name="images"
													render={({ field: { onChange, value } }) => {
														const handleChange = (uploadedFiles?: File[]) => {
															onChange([...(value ?? []), ...(uploadedFiles ?? [])]);
															scrollToBottom();
														};

														return (
															<FileUpload
																acceptedTypes={{
																	'image/jpeg': [],
																	'image/png': [],
																}}
																onChange={handleChange}
																maxFiles={MAX_FILES - (formImages?.length ?? 0)}
															/>
														);
													}}
												/>
											)}

											{(formImages?.length ?? -1) > 0 && (
												<UploadedImages formImages={formImages} onFormImagesChange={setValue} />
											)}
										</Stack>
									}
								/>
							)}
							{isMobileFormat && formModule && !!formType && (
								<Button
									disabled={!isValid}
									error={hasSendTicketOrSuggestionError}
									loading={isMailSending}
									onClick={handleSubmit(onSubmit)}
									persistentErrorMessage={supportButtonError}
									startIcon={<Icon path={mdiSendOutline} size="small" />}
									success={hasSendTicketOrSuggestionSucceeded}
									variant="contained"
								>
									{supportButtonLabel}
								</Button>
							)}
						</Stack>
					</Stack>
				)}
			</>
		),
		[
			isMobileFormat,
			appName,
			isSignModuleAvailable,
			control,
			formType,
			moreInfoHeight,
			isValid,
			hasSendTicketOrSuggestionError,
			isMailSending,
			handleSubmit,
			onSubmit,
			supportButtonError,
			hasSendTicketSucceeded,
			supportButtonLabel,
			formModule,
			moduleInfoHeight,
			formImages,
			setValue,
			hasSendTicketOrSuggestionSucceeded,
			reset,
			sendSuggestionsError,
			sendTicketError,
		],
	);

	const actionsDialog: DialogProps['actionsDialog'] = [
		{
			id: 'cancel',
			disabled: isMailSending,
			label: hasSendTicketSucceeded || !!sendSuggestionsData ? 'Fermer' : 'Annuler',
			onClick: closeDialog,
		},
		{
			id: 'send',
			disabled: !isValid,
			error: hasSendTicketOrSuggestionError,
			label: supportButtonLabel,
			loading: isMailSending,
			onClick: handleSubmit(onSubmit),
			persistentErrorMessage: supportButtonError,
			startIcon: <Icon path={mdiSendOutline} size="small" />,
			success: hasSendTicketOrSuggestionSucceeded,
			variant: 'contained',
		},
	];

	/**
	 * These useEffects ensure that newly rendered form sections are scrolled into view on mobile.
	 * We cannot simply scroll on button clicks, because the corresponding blocks are rendered
	 * conditionally and may not exist in the DOM yet at the time of the click.
	 * Using useEffect guarantees that the scroll happens right after the element is mounted.
	 */
	useEffect(() => {
		if (isMobileFormat && formType && moreInfoRef.current) {
			moreInfoRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}, [formType, isMobileFormat]);

	useEffect(() => {
		if (isMobileFormat && !!formModule && !!formType && formContentContainerRef.current) {
			formContentContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}, [formModule, formType, isMobileFormat]);

	return isMobileFormat ? (
		<Stack padding={2} ref={supportContentRef}>
			<StyledSupportHeader>
				<Typography fontWeight="bold" variant="h6">
					Support
				</Typography>
			</StyledSupportHeader>
			<Stack flex={1} gap={2}>
				{supportContent}
			</Stack>
		</Stack>
	) : (
		<StyledDialog
			actionsDialog={actionsDialog}
			title="Support"
			open={isDialogOpen}
			onClose={handleCloseEvent}
			fullWidth
		>
			<Stack flex={1} gap={2} ref={supportContentRef} paddingBottom={1}>
				{supportContent}
			</Stack>
		</StyledDialog>
	);
};

export default Support;
